import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faCheck, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { getAllExperienceTypes } from '../../data/experienceTypes';
import BottomSheet from '../BottomSheet';

// Simple candle lighting time calculation (Friday sunset)
function getCandleLightingTime() {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 = Sunday, 5 = Friday
  const daysUntilFriday = (5 - dayOfWeek + 7) % 7 || 7;
  
  // If it's already Friday after 4pm, show today's time
  const isFriday = dayOfWeek === 5;
  const isAfternoon = today.getHours() >= 16;
  
  if (isFriday && isAfternoon) {
    return '4:45 PM'; // Today
  }
  
  // Default to Friday at 4:45 PM (simplified - in real app would use location/timezone)
  return '4:45 PM';
}

const quickChecklist = [
  { id: 'challah', label: 'Challah', checked: false },
  { id: 'wine', label: 'Wine for Kiddush', checked: false },
  { id: 'candles', label: 'Shabbat Candles', checked: false },
  { id: 'menu', label: 'Dinner Menu Planned', checked: false },
];

export default function ShabbatPlanningModal({ isOpen, onClose, onCreateExperience }) {
  const [checklist, setChecklist] = useState(quickChecklist);
  const [candleTime] = useState(getCandleLightingTime());
  const navigate = useNavigate();
  const experienceTypes = getAllExperienceTypes();
  const shabbatExperience = experienceTypes.find(e => e.id === 'shabbat-dinners');

  const handleToggleCheck = (id) => {
    setChecklist(prev =>
      prev.map(item =>
        item.id === id ? { ...item, checked: !item.checked } : item
      )
    );
  };

  const handleCreateExperience = () => {
    if (onCreateExperience && shabbatExperience) {
      onCreateExperience('shabbat-dinners');
    }
    onClose();
  };

  const checkedCount = checklist.filter(item => item.checked).length;
  const allChecked = checkedCount === checklist.length;

  if (!isOpen) return null;

  // Content component to be used in both BottomSheet and regular modal
  const ModalContent = () => (
    <div className="p-4 md:p-6 md:pb-8">

      {/* Candle Lighting Time */}
      <div className="bg-primary/10 rounded-xl p-4 mb-6">
        <div className="text-sm text-text-secondary mb-1">Candle Lighting Time</div>
        <div className="text-xl md:text-2xl font-semibold text-primary">
          {candleTime}
        </div>
        <div className="text-xs text-text-secondary mt-1">
          {new Date().getDay() === 5 ? 'Today' : 'This Friday'}
        </div>
      </div>

      {/* Quick Checklist */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-text-primary">Quick Checklist</h3>
          <div className="text-sm text-text-secondary">
            {checkedCount}/{checklist.length}
          </div>
        </div>
        <div className="space-y-2">
          {checklist.map((item) => (
            <button
              key={item.id}
              onClick={() => handleToggleCheck(item.id)}
              className="w-full flex items-center gap-3 p-3 bg-surface hover:bg-[#F0F0F0] rounded-xl transition-colors text-left touch-target active:scale-95"
            >
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                item.checked
                  ? 'bg-primary border-primary'
                  : 'border-border'
              }`}>
                {item.checked && (
                  <FontAwesomeIcon icon={faCheck} className="text-white text-xs" />
                )}
              </div>
              <span className={`text-sm font-medium ${
                item.checked ? 'text-text-secondary line-through' : 'text-text-primary'
              }`}>
                {item.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-3">
        <button
          onClick={handleCreateExperience}
          className="w-full px-4 py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary-dark transition-colors flex items-center justify-center gap-2 touch-target active:scale-95"
        >
          <FontAwesomeIcon icon={faPlus} />
          <span>Create Full Shabbat Project</span>
        </button>
        {allChecked && (
          <div className="text-center text-sm text-text-secondary">
            ✨ You're all set for Shabbat!
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile: BottomSheet */}
      <BottomSheet
        isOpen={isOpen}
        onClose={onClose}
        title="Quick Shabbat Planning"
        fullScreen={false}
      >
        <ModalContent />
      </BottomSheet>

      {/* Desktop: Regular Modal (hidden on mobile) */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="hidden md:flex fixed inset-0 bg-black/50 z-50 items-center justify-center p-4"
            onClick={onClose}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-border">
                <h2 className="text-xl sm:text-2xl font-semibold text-text-primary">
                  Quick Shabbat Planning
                </h2>
                <button
                  onClick={onClose}
                  className="w-8 h-8 flex items-center justify-center text-text-secondary hover:text-text-primary transition-colors touch-target"
                  aria-label="Close"
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
              <ModalContent />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

